import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import client from '../api/client';
import { useAuth } from './AuthContext';
import { primaryPhoto } from '../utils/photos';
import { EMPTY_CART, normalizeServerCart, normalizeGuestItems } from '../utils/cart';
import { readGuestCart, writeGuestCart, clearGuestCart } from '../storage/cart';

// Cart for both guests (localStorage) and registered users (server-synced).
// On login, a non-empty guest cart is merged into the server cart, then dropped.
const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const userId = user?.id || null;

  const [cart, setCart] = useState(EMPTY_CART);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  const openDrawer = useCallback(() => setIsOpen(true), []);
  const closeDrawer = useCallback(() => setIsOpen(false), []);

  const reloadServerCart = useCallback(async () => {
    const { data } = await client.get('/cart');
    setCart(normalizeServerCart(data));
  }, []);

  // Public refresh — re-pull the authoritative cart (e.g. after an order is
  // paid, the server empties it and the badge should update).
  const refresh = useCallback(async () => {
    if (userId) await reloadServerCart();
    else setCart(normalizeGuestItems(readGuestCart()));
  }, [userId, reloadServerCart]);

  // Load the right cart when auth settles / user changes. Merges guest -> server
  // on login (guest cart is cleared once merged, so this never double-merges).
  useEffect(() => {
    if (authLoading) return undefined;
    let active = true;

    (async () => {
      setLoading(true);
      try {
        if (userId) {
          const guest = readGuestCart();
          if (guest.length) {
            const { data } = await client.post('/cart/merge', {
              items: guest.map((i) => ({ productId: i.productId, quantity: i.quantity })),
            });
            clearGuestCart();
            if (active) setCart(normalizeServerCart(data));
          } else {
            const { data } = await client.get('/cart');
            if (active) setCart(normalizeServerCart(data));
          }
        } else if (active) {
          setCart(normalizeGuestItems(readGuestCart()));
        }
      } catch {
        if (active) setCart(userId ? EMPTY_CART : normalizeGuestItems(readGuestCart()));
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [userId, authLoading]);

  // --- guest helpers ---
  const persistGuest = (items) => {
    writeGuestCart(items);
    setCart(normalizeGuestItems(items));
  };

  const addItem = useCallback(
    async (product, quantity = 1) => {
      const productId = product._id || product.id;
      if (userId) {
        const { data } = await client.post('/cart/items', { productId, quantity });
        setCart(normalizeServerCart(data));
      } else {
        const items = readGuestCart();
        const idx = items.findIndex((i) => i.productId === productId);
        if (idx >= 0) {
          items[idx] = { ...items[idx], quantity: items[idx].quantity + quantity };
        } else {
          items.push({
            productId,
            title: product.title,
            unitPrice: product.price,
            quantity,
            photoUrl: primaryPhoto(product.photos)?.url || null,
          });
        }
        persistGuest(items);
      }
      setIsOpen(true);
    },
    [userId]
  );

  const setQuantity = useCallback(
    async (productId, quantity) => {
      if (quantity <= 0) {
        // fall through to removeItem behavior
        if (userId) {
          setCart((prev) => optimistic(prev, (items) => items.filter((i) => i.productId !== productId)));
          try {
            const { data } = await client.delete(`/cart/items/${productId}`);
            setCart(normalizeServerCart(data));
          } catch {
            await reloadServerCart();
          }
        } else {
          persistGuest(readGuestCart().filter((i) => i.productId !== productId));
        }
        return;
      }

      if (userId) {
        // optimistic update, then reconcile with the server response
        setCart((prev) =>
          optimistic(prev, (items) =>
            items.map((i) => (i.productId === productId ? { ...i, quantity } : i))
          )
        );
        try {
          const { data } = await client.put(`/cart/items/${productId}`, { quantity });
          setCart(normalizeServerCart(data));
        } catch {
          await reloadServerCart();
        }
      } else {
        persistGuest(
          readGuestCart().map((i) => (i.productId === productId ? { ...i, quantity } : i))
        );
      }
    },
    [userId, reloadServerCart]
  );

  const removeItem = useCallback((productId) => setQuantity(productId, 0), [setQuantity]);

  const clearCart = useCallback(async () => {
    if (userId) {
      try {
        const { data } = await client.delete('/cart');
        setCart(normalizeServerCart(data));
      } catch {
        await reloadServerCart();
      }
    } else {
      clearGuestCart();
      setCart(normalizeGuestItems([]));
    }
  }, [userId, reloadServerCart]);

  const value = useMemo(
    () => ({
      items: cart.items,
      subtotal: cart.subtotal,
      count: cart.count,
      source: cart.source,
      depositTotal: cart.depositTotal,
      amountDueNow: cart.amountDueNow,
      balanceDue: cart.balanceDue,
      hasLocalPickupItems: cart.hasLocalPickupItems,
      hasShippableItems: cart.hasShippableItems,
      requiresFulfillmentChoice: cart.requiresFulfillmentChoice,
      pickupDepositPercent: cart.pickupDepositPercent,
      loading,
      isOpen,
      openDrawer,
      closeDrawer,
      addItem,
      setQuantity,
      removeItem,
      clearCart,
      refresh,
    }),
    [cart, loading, isOpen, openDrawer, closeDrawer, addItem, setQuantity, removeItem, clearCart, refresh]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

// Recompute totals locally for optimistic UI (mirrors utils/cart + server deposit
// math). The server response that follows shortly after is authoritative.
function optimistic(prev, mutate) {
  const items = mutate(prev.items)
    .filter((i) => i.quantity > 0)
    .map((i) => {
      const lineTotal = i.unitPrice * i.quantity;
      const depositAmount = i.depositRequired ? Math.round((lineTotal * (i.depositPercent || 0)) / 100) : 0;
      const balanceDue = i.depositRequired ? lineTotal - depositAmount : 0;
      return { ...i, lineTotal, depositAmount, balanceDue };
    });
  const available = items.filter((i) => i.available !== false);
  const subtotal = available.reduce((s, i) => s + i.lineTotal, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);
  const depositTotal = available.reduce((s, i) => s + (i.depositRequired ? i.depositAmount : 0), 0);
  const balanceDue = available.reduce((s, i) => s + i.balanceDue, 0);
  const amountDueNow = subtotal - balanceDue;
  const hasLocalPickupItems = available.some((i) => i.localPickupOnly);
  const hasShippableItems = available.some((i) => !i.localPickupOnly);
  const requiresFulfillmentChoice = hasLocalPickupItems && hasShippableItems;
  return {
    ...prev,
    items,
    subtotal,
    count,
    depositTotal,
    amountDueNow,
    balanceDue,
    hasLocalPickupItems,
    hasShippableItems,
    requiresFulfillmentChoice,
  };
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return ctx;
}
